import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { LightBulbBase } from '@doist/icons/brand';
import { Divider, Heading, HelpCenterNote, slugifyText, Text } from '@doist/marketist';
import { Blockquote } from 'components/blog-posts/blockquote/blockquote';
import { CustomHtml } from 'components/blog-posts/custom-html/custom-html';
import { ZoomableImage } from 'components/blog-posts/image/image';
import { Markup } from 'components/blog-posts/markup/markup';
import { RichTextCTA } from 'components/blog-posts/rich-text-cta/rich-text-cta';
import { RichTextHighlight } from 'components/blog-posts/rich-text-highlight/rich-text-highlight';
import { Table } from 'components/blog-posts/table/table';
import Td from 'components/blog-posts/td/td';
import { TipCard } from 'components/blog-posts/tip-card/tip-card';
import { marked } from 'marked';
import { getRecursiveChildText } from 'services/get-recursive-child-text';
import type { Components } from '@doist/content-system';
import type { FC, ReactNode } from 'react';
import type { RichTextCTAData } from 'types/contentful';
import type { BlogArticleBox, BlogEmbedHtml, BlogTip, Image } from './rules';
interface CustomComponents extends Components {
  blogArticleBox: FC<BlogArticleBox>;
  blogTip: FC<BlogTip>;
  blogEmbedHtml: FC<BlogEmbedHtml>;
  img: FC<Image>;
  blogRichTextCta: FC<RichTextCTAData>;
  blogRichText: FC<{
    text: string;
    type: string;
  }>;
  articleTable: FC<{
    children: ReactNode;
  }>;
}
export function getComponents(blogTranslation: (text: string) => string): CustomComponents {
  function translateChildren(children: React.ReactNode): React.ReactNode {
    if (typeof children === 'string') {
      return blogTranslation(children);
    }
    if (Array.isArray(children)) {
      return children.map(translateChildren);
    }
    return children;
  }
  return {
    p: ({
      children
    }) => <Text size="body-base">
                <Markup tagName="fragment" content={blogTranslation(renderToStaticMarkup(children))} />
            </Text>,
    h2: ({
      children
    }) => <Heading level="2" id={slugifyText(getRecursiveChildText(children))} size="major-base">
                {translateChildren(children)}
            </Heading>,
    h3: ({
      children
    }) => <Heading level="3" id={slugifyText(getRecursiveChildText(children))} size="major-sm">
                {translateChildren(children)}
            </Heading>,
    blockquote: ({
      children
    }) => <Blockquote>{children}</Blockquote>,
    hr: () => <Divider />,
    th: ({
      children
    }) => <th>
                <b>{children}</b>
            </th>,
    td: ({
      children
    }) => <Td>{children}</Td>,
    img: props => <ZoomableImage src={props.url} width={props.size.width / 2} height={props.size.height / 2} alt={blogTranslation(props.title)} caption={blogTranslation(props.description)} />,
    // Custom components
    articleTable: ({
      children
    }) => <Table>{children}</Table>,
    blogEmbedHtml: props => <CustomHtml html={props.html} />,
    blogTip: props => <TipCard title={blogTranslation(props.title)} description={blogTranslation(props.description)} ctaLink={props.ctaLink ? {
      ...props.ctaLink,
      text: blogTranslation(props.ctaLink.text)
    } : undefined} captionLink={props.captionLink ? {
      ...props.captionLink,
      text: blogTranslation(props.ctaLink.text)
    } : undefined} backgroundImage={props.backgroundImage ? {
      ...props.backgroundImage,
      alt: props.backgroundImage.alt ? blogTranslation(props.backgroundImage.alt) : undefined
    } : undefined} />,
    blogArticleBox: props => props.type === 'dig-deeper' ? <RichTextCTA title={blogTranslation(props.title)} description={blogTranslation(props.content)} illustration={props.illustration} button={props.link ? {
      text: blogTranslation(props.link.title),
      href: props.link.href
    } : undefined} /> : <HelpCenterNote title={<Text size="minor-base" weight="semibold">
                            {blogTranslation(props.title.trim())}
                        </Text>} icon={<LightBulbBase />} variant={props.type}>
                    <Markup tagName="fragment" content={marked(blogTranslation(props.content as string)) as string} />
                </HelpCenterNote>,
    blogRichTextCta: props => <RichTextCTA title={blogTranslation(props.title)} description={blogTranslation(props.description)} backgroundColor={props.backgroundColor === 'green' ? '#faf8f0' : '#fff6f0'} illustration={`https:${props.illustration.fields.file.url}`} button={props.callToAction ? {
      text: blogTranslation(props.callToAction.fields.title),
      href: props.callToAction.fields.url
    } : undefined} />,
    blogRichText: ({
      text,
      type
    }) => type === 'highlight' ? <RichTextHighlight>{blogTranslation(text)}</RichTextHighlight> : null
  };
}